import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Creatable } from "react-select-plus";
import styled from "styled-components";

import { CTag, Box, Row } from "@appcues/component-library";
import { addTagToFlow, removeTagFromFlow } from "actions/account/flows";
import { createTag } from "actions/account/tags";

import { getFilteredTagsSelectionForStep } from "helpers/steps";
import { isTagNameUnique } from "helpers/tags";
import { asPromised } from "utils/as-promised";
import { stripNonAlphaNumericChars } from "utils/validation";
import AddTagControl from "./AddTagControl";

export class StepTagControl extends Component {
  state = {
    shouldShowTagSelector: false
  };

  toggleTagSelector = willShowTagSelector => {
    const { shouldShowTagSelector } = this.state;

    if (willShowTagSelector !== shouldShowTagSelector) {
      this.setState({ shouldShowTagSelector: !shouldShowTagSelector });
    }
  };

  handleAddTag = ({ label: tagName, value: tagId }) => {
    const { tags, onAddTag, onCreateTag } = this.props;

    if (!tagId || !tagName) return;

    const tagIsUnique = isTagNameUnique(tagName, tags);

    if (tagIsUnique) {
      onCreateTag(tagName).then(onAddTag);
    } else {
      onAddTag(tagId);
    }

    this.setState({ shouldShowTagSelector: false });
  };

  handleDeleteTag = tagId => {
    const { onRemoveTag } = this.props;
    onRemoveTag(tagId);
    this.setState({ shouldShowTagSelector: false });
  };

  handleInputKeyDown = event => {
    const { shouldShowTagSelector } = this.state;

    switch (event.keyCode) {
      case 27: // ESC
        if (shouldShowTagSelector) {
          this.toggleTagSelector(false);
        }
        break;
      default:
        break;
    }
  };

  validateOption = currentOption => {
    if (!currentOption || !currentOption.label) return false;
    const { tags } = this.props;
    return isTagNameUnique(currentOption.label, tags);
  };

  createTagLabel = newTag => {
    return !newTag ? "" : `Press Enter to add "${newTag}"`;
  };

  handleInputChange = input => {
    return stripNonAlphaNumericChars(input);
  };

  render() {
    const { stepTags, tagsDropdownItems, ...boxProps } = this.props;
    const { shouldShowTagSelector } = this.state;

    const options = getFilteredTagsSelectionForStep(
      stepTags,
      tagsDropdownItems
    );

    return (
      <Box width="100%" onClick={e => e.stopPropagation()} {...boxProps}>
        <Row gutter={8}>
          {stepTags &&
            stepTags.length > 0 &&
            stepTags.map(stepTag => (
              <CTag
                title={stepTag.name}
                key={stepTag.id}
                onClickRemove={() => this.handleDeleteTag(stepTag.id)}
              >
                {stepTag.name}
              </CTag>
            ))}

          <div
            className="add-tag-container"
            data-testid="add-tag-container"
            style={shouldShowTagSelector ? { opacity: 1 } : null}
          >
            {!shouldShowTagSelector ? (
              <AddTagControl
                toggleTagSelector={() => this.toggleTagSelector(true)}
              />
            ) : (
              <div
                className="tag-selection-menu"
                style={{ position: "absolute" }}
                onClick={e => {
                  e.stopPropagation();
                }}
              >
                <div className="tags-container" data-testid="tags-container">
                  <Creatable
                    searchable
                    clearable
                    openOnFocus
                    optionClassName="tag-item"
                    options={options}
                    onBlur={() => this.toggleTagSelector(false)}
                    onChange={this.handleAddTag}
                    onInputChange={this.handleInputChange}
                    onInputKeyDown={this.handleInputKeyDown}
                    isValidNewOption={this.validateOption}
                    placeholder="Select a tag"
                    promptTextCreator={this.createTagLabel}
                  />
                </div>
              </div>
            )}
          </div>
        </Row>
      </Box>
    );
  }
}

StepTagControl.propTypes = {
  tags: PropTypes.objectOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })
  ),
  stepTags: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })
  ),
  tagsDropdownItems: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
  ),
  onAddTag: PropTypes.func,
  onRemoveTag: PropTypes.func,
  onCreateTag: PropTypes.func
};

const mapDispatchToProps = (dispatch, { stepId: flowId }) => ({
  onAddTag: tagId => dispatch(addTagToFlow({ flowId, tagId })),
  onRemoveTag: tagId => dispatch(removeTagFromFlow({ flowId, tagId })),
  onCreateTag: tagName => asPromised(dispatch, createTag(tagName))
});

const ConnectedStepTagControl = connect(
  null,
  mapDispatchToProps
)(
  // language=SCSS prefix=dummy{ suffix=}
  styled(StepTagControl)`
    &.step-tag-controls {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      position: relative;
      width: 100%;

      > i {
        margin-right: 8px;
        color: ${props => props.theme["$gray-50"]};
      }
    }

    .add-tag-container {
      opacity: 0;
      transition: opacity 0.15s ease;
    }

    .tags-icon {
      margin-left: 0px;

      svg path {
        fill: ${props => props.theme["$gray-30"]};
      }
    }

    .delete-icon {
      display: inline-flex; /* ? */
      vertical-align: middle;
      margin-right: 0;

      svg path {
        fill: ${props => props.theme["$gray-30"]};
      }

      &:hover svg path {
        fill: ${props => props.theme["$gray-3"]};
      }
    }

    .add-icon {
      svg path {
        fill: ${props => props.theme["$gray-30"]};
      }
      &:hover svg path {
        fill: ${props => props.theme["$green"]};
      }
    }

    .step-tag {
      margin: 2px;
      border-radius: 12px;
      letter-spacing: 0;
      padding: 2px 4px 2px 7px;

      span {
        vertical-align: middle;
      }
      > i {
        display: inline;
        vertical-align: middle;
        padding-left: 8px;
      }
    }

    .tag-selection-menu {
      display: flex;
      align-self: flex-start;
      position: absolute;
      margin: 0 8px;
      z-index: 10;

      .tags-container {
        max-height: 200px;
        min-width: 150px;
      }

      .Select.is-focused:not(.is-open) > .Select-control {
        box-shadow: none;
      }

      .Select-control,
      .Select.is-open > .Select-control,
      .Select.is-focused > .Select-control {
        border: 1px solid ${props => props.theme["$gray-30"]};
      }

      .Select-menu-outer {
        position: relative;
        z-index: 15;
      }

      .tag-item {
        padding: 4px 16px;
        background-color: white;

        &:first-child {
          padding-top: 8px;
        }

        &:last-child {
          padding-bottom: 8px;
        }
      }
    }
  `
);

ConnectedStepTagControl.propTypes = {
  stepId: PropTypes.string,
  tags: PropTypes.objectOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })
  ),
  stepTags: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })
  ),
  tagsDropdownItems: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
  )
};

export default ConnectedStepTagControl;
